import type {
  InsurerFormEntry,
  InsurerFormSummary,
  InsurerFormTemplate,
  Placement,
  PlacementUpdate,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob } from '../../lib/api';
import { refreshLists } from '../../lib/list';
import { useSession } from '../../lib/session';
import { save } from '../evidence/api';

/** Insurers' proposal forms for an assessment, and placing the client with each insurer (tasks 88–93). */

const formsKey = (t: string | undefined, id: string) => ['insurer-forms', t, id] as const;
const catalogueKey = (t: string | undefined) => ['insurer-forms', t, 'catalogue'] as const;

export function useInsurerForms(id: string) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: formsKey(tenant?.id, id),
    queryFn: () => api<InsurerFormSummary[]>('GET', `/assessments/${id}/insurer-forms`),
    enabled: !!tenant,
  });
}

export function usePlace(id: string, key: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (body: PlacementUpdate) => api<Placement>('PUT', `/assessments/${id}/insurer-forms/${key}/placement`, body), // prettier-ignore
    onSuccess: (placement) => {
      qc.setQueryData<InsurerFormSummary[]>(formsKey(tenant?.id, id), (forms) => forms?.map((f) => (f.key === key ? { ...f, placement } : f))); // prettier-ignore
      // A quote or a binding changes the client's record and the clients list.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void refreshLists(qc);
    },
  });
}

/** Downloads the insurer's PDF filled from the answers; with `annex`, the submission pack. */
export async function downloadInsurerPdf(
  id: string,
  form: Pick<InsurerFormSummary, 'key' | 'form_code'>,
  customer: string,
  annex: boolean,
) {
  // prettier-ignore
  const blob = await apiBlob(`/assessments/${id}/insurer-forms/${form.key}/pdf${annex ? '?annex=1' : ''}`);
  save(blob, `${form.form_code} ${customer}${annex ? ' with annex' : ''}.pdf`.replace(/[\\/:*?"<>|]+/g, ' '));
}

export function useInsurerCatalogue(enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: catalogueKey(tenant?.id),
    queryFn: () => api<InsurerFormEntry[]>('GET', '/insurer-forms'),
    enabled: !!tenant && enabled,
  });
}

export function useUploadTemplate() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: ({ key, file }: { key: string; file: File }) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api<InsurerFormTemplate>('PUT', `/insurer-forms/templates/${key}`, form);
    },
    onSuccess: (template) => {
      qc.setQueryData<InsurerFormEntry[]>(catalogueKey(tenant?.id), (all) => all?.map((f) => (f.key === template.form_key ? { ...f, template } : f))); // prettier-ignore
    },
  });
}
